import { Component, inject, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { SidebarComponent } from '../../../common/sidebar/sidebar.component';
import { ErrorCodeTableComponent } from '../../../tables/error-code/error-code-table/error-code-table.component';
import { CrudOverviewBaseComponent } from '../../../base/crud-overview-base.component';
import { ErrorCode } from '../../../../../shared/models/error-code/error-code';
import { OnUnsavedChanges } from '../../../../../shared/internal/on-unsaved-changes';
import { DetailsViewConfiguration } from '../../../../../shared/models/internal/details/details-view-configuration';
import { Router } from '@angular/router';
import { ErrorCodeApiService } from '../../../../services/api/error-code-api.service';
import { STORAGE_KEYS } from '../../../../../shared/constants/storage-keys';
import { ErrorCodeQuery } from '../../../../../shared/models/error-code/error-code-query';
import { CommonModule, DatePipe } from '@angular/common';
import { ErrorCodeWithQuey } from '../../../../../shared/models/error-code/error-code-with-query';
import { EditComponentType } from '../../../../../shared/enums/edit-component-type';
import { TranslationService } from '../../../../services/translation.service';
import { SidebarConfiguration } from '../../../../../shared/internal/sidebar/sidebar-configuration';
import { buildErrorCodeDetailsConfiguration } from '../../../../common/error-code-details-view-configuration';
import { EnabledByPolicyDirective } from '../../../../directives/enabled-by-policy.directive';
import { AddComponent } from '../../../icons/add/add.component';
import { AgentActionDirective } from '../../../../directives/agent-action.directive';
import { prefixWithSlash } from '../../../../../shared/internal/prefix-with-slash';
import { ROUTE_PATHS } from '../../../../../shared/constants/route-paths';
import { Policy } from '../../../../../shared/models/info/policy';
import { AccessRightsService } from '../../../../services/access-rights.service';
import { Observable, Subscription } from 'rxjs';
import { Pagination } from '../../../../../shared/models/internal/pagination';
import { errorToString } from '../../../../common/common-utils';

@Component({
  selector: 'app-error-code-overview',
  imports: [
    SidebarComponent,
    ErrorCodeTableComponent,
    CommonModule,
    EnabledByPolicyDirective,
    AddComponent,
    AgentActionDirective,
  ],
  templateUrl: './error-code-overview.component.html',
  styleUrl: './error-code-overview.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [DatePipe],
})
export class ErrorCodeOverviewComponent
  extends CrudOverviewBaseComponent<ErrorCode>
  implements OnUnsavedChanges<ErrorCode>, OnDestroy
{
  public errorCodeDetailConfiguration?: DetailsViewConfiguration;
  public errorCodeEditComponent = EditComponentType.ErrorCode;
  public errorCodeEditComponentId?: string;
  public sidebarConfiguration: SidebarConfiguration = new SidebarConfiguration();

  protected recyclingModeEnabled = false;

  protected readonly errorCodeApi = inject(ErrorCodeApiService);
  protected readonly router = inject(Router);
  protected readonly translationService = inject(TranslationService);

  private readonly accessRightService = inject(AccessRightsService);
  private readonly datePipe = inject(DatePipe);

  private languageServiceSubscription?: Subscription;
  private currentQuery?: ErrorCodeQuery;

  constructor() {
    super(STORAGE_KEYS.UNSAVED_ERROR_CODE_CHANGES);
    this.languageServiceSubscription = this.translationService.languageChangeSubject.subscribe({
      next: async () => {
        if (this.currentQuery) {
          if (this.selectedTableElement.isElementSelected) {
            const requestedPage = this.selectedTableElement.currentPage || 0;
            await this.fetchErrorCodesUntilPage(this.currentQuery, requestedPage);
            this.handleNewSelection(this.selectedTableElement.selectedElementId!);
          } else {
            await this.fetchErrorCodesUntilPage(this.currentQuery, 0);
          }
        }
      },
    });
  }

  public override ngOnInit(): void {
    super.ngOnInit();
    this.sidebarConfiguration.editAreaEnabled = !this.recyclingModeEnabled;
    this.sidebarConfiguration.translationsAreaEnabled = false;
    this.sidebarConfiguration.detailedTranslationsAreaEnabled = true;
    this.configureReviewsEnabled();
    this.configureMergeReviewsEnabled();
  }

  public override ngOnDestroy(): void {
    if (this.languageServiceSubscription) {
      this.languageServiceSubscription.unsubscribe();
    }
  }

  public onSearchTriggered(query: ErrorCodeQuery): void {
    const blocked = this.areThereUnsavedChanges(this.getFriendlyItemName(this.selectedItem), () => {
      this.search(query);
    });

    if (!blocked) {
      this.search(query);
    }
  }

  public onSortByChanged(query: ErrorCodeQuery): void {
    this.onSearchTriggered(query);
  }

  public onScrolledToTheBottom(query: ErrorCodeQuery): void {
    this.fetchErrorCodes(query, false);
  }

  public onUpdatedErrorCode(ec: ErrorCode): void {
    this.refreshItemInListAfterUpdate(ec);
    this.selectedItem = ec;
    if (ec.id) {
      this.selectedTableElement.setSelectedElementId(ec.id, this.paginationService.currentPageNumber);
    }
    this.clearUnsavedData();
    this.handleNewSelection(ec.id ?? '');
  }

  public onSoftDeleteTriggered(event: ErrorCodeWithQuey): void {
    this.errorCodeApi.softDeleteErrorCode(event.errorCode?.id ?? '').subscribe({
      next: () => {
        this.removeItemFromList(event.errorCode?.id);
        this.sidebarOpen = false;
        this.toastr.success('ErrorCode soft deleted');
      },
      error: (error) => {
        console.error('Error soft deleting ErrorCode:', errorToString(error));
      },
    });
  }

  public onHardDeleteTriggered(event: ErrorCodeWithQuey): void {
    this.errorCodeApi.deleteErrorCode(event.errorCode?.id ?? '').subscribe({
      next: () => {
        this.removeItemFromList(event.errorCode?.id);
        this.sidebarOpen = false;
        this.toastr.success('ErrorCode deleted');
      },
      error: (error) => {
        console.error('Error deleting ErrorCode:', errorToString(error));
      },
    });
  }

  public onRestoreTriggered(event: ErrorCodeWithQuey): void {
    this.errorCodeApi.restoreErrorCode(event.errorCode?.id ?? '').subscribe({
      next: () => {
        let query = event.query;
        if (query == undefined) {
          query = {
            searchPattern: '',
          } as ErrorCodeQuery;
        }
        this.fetchErrorCodes(query, true);
        this.sidebarOpen = false;
        this.toastr.success('ErrorCode restored');
      },
      error: (error) => {
        console.error('Error restoring ErrorCode:', errorToString(error));
      },
    });
  }

  public createErrorCode(): void {
    this.router.navigate([prefixWithSlash(ROUTE_PATHS.errorCodeNew)]);
  }

  protected override handleNewSelection(id: string): void {
    this.configureReviewsEnabled();
    this.configureMergeReviewsEnabled();
    this.sidebarOpen = true;
    this.sideBarTitle = `Error Code ${this.getFriendlyItemName(this.getItemById(id))}`;

    this.errorCodeApi.getErrorCode(id).subscribe({
      next: (ec) => {
        this.configureDetailsView(ec);
        this.errorCodeEditComponentId = ec.id;
        this.selectedItem = ec;
      },
      error: (error) => {
        console.error('Error fetching ErrorCode:', errorToString(error));
      },
    });
  }

  protected override getFriendlyItemName(item?: ErrorCode): string {
    if (item) {
      return item.displayId ?? '';
    }
    return '';
  }

  protected override configureDetailsView(item: ErrorCode): void {
    this.errorCodeDetailConfiguration = buildErrorCodeDetailsConfiguration(
      item,
      this.translationService.defaultLanguageSelected(),
      this.datePipe
    );
  }

  protected async fetchErrorCodes(query: ErrorCodeQuery, resetPagination: boolean): Promise<void> {
    this.currentQuery = query;
    await this.fetchPagedData(resetPagination, (page, pageSize) => this.getErrorCodes(query, page, pageSize));
  }

  protected async fetchErrorCodesUntilPage(query: ErrorCodeQuery, targetPage: number): Promise<void> {
    this.currentQuery = query;
    await this.fetchPagedDataUntilPage(targetPage, (page, pageSize) => this.getErrorCodes(query, page, pageSize));
  }

  protected configureReviewsEnabled(): void {
    this.accessRightService.getAccessRights$().subscribe((ar) => {
      this.sidebarConfiguration.reviewAreaEnabled =
        this.translationService.defaultLanguageSelected() &&
        (ar.authorized(Policy.canCreateTechnicalOrganisationDtcReviewRequests) ||
          ar.authorized(Policy.canCreateAfterSalesDtcReviewRequests));
    });
  }

  protected configureMergeReviewsEnabled(): void {
    this.accessRightService.getAccessRights$().subscribe((ar) => {
      this.sidebarConfiguration.mergeReviewAreaEnabled =
        this.translationService.defaultLanguageSelected() && ar.authorized(Policy.canMergeDtcReviewRequests);
    });
  }

  private getErrorCodes(query: ErrorCodeQuery, page: number, pageSize: number): Observable<Pagination<ErrorCode>> {
    query.page = page;
    query.pageSize = pageSize;
    query.softDeleted = this.recyclingModeEnabled;
    return this.errorCodeApi.getErrorCodes(
      query,
      TranslationService.defaultLanguageCode(),
      this.translationService.getLanguage()
    );
  }

  private refreshItemInListAfterUpdate(ec?: ErrorCode): void {
    const code = ec?.code;
    const nwNumber = (ec?.nwNumbers?.length ?? 0 > 0) ? ec!.nwNumbers![0].number : null;
    if (!code || !nwNumber) {
      return;
    }
    const query = {
      codes: code,
      nwNumber: nwNumber,
    } as ErrorCodeQuery;
    this.errorCodeApi
      .getErrorCodes(query, TranslationService.defaultLanguageCode(), this.translationService.getLanguage())
      .subscribe({
        next: (errorCodes) => {
          const updatedErrorCode = errorCodes.items.shift();
          if (updatedErrorCode) {
            this.items = this.items?.map((existingErrorCode) =>
              existingErrorCode.id === updatedErrorCode.id ? updatedErrorCode : existingErrorCode
            );
          }
        },
        error: (error) => {
          console.error(errorToString(error));
        },
      });
  }

  private search(query: ErrorCodeQuery): void {
    this.items = [];
    this.sidebarOpen = false;
    this.selectedItem = undefined;
    this.selectedTableElement.clearSelection();
    this.fetchErrorCodes(query, true);
  }
}
